import { Component, computed, inject, signal, Type } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { CountriesService, Country } from '@vectronic/ui';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';

export interface FormlyCountriesAutocompleteFieldConfig extends FormlyFieldConfig<FormlyFieldProps> {
  type: 'countries-autocomplete' | Type<CountriesAutocompleteComponent>;
}

@Component({
  selector: 'vectronic-formly-countries-autocomplete',
  imports: [
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    MatInput,
    FormlyModule,
    MatAutocompleteModule,
    MatError,
  ],
  template: `<mat-form-field>
    <mat-label>{{ props?.label ?? 'Animal Species' }}</mat-label>
    <input
      #input
      matInput
      [id]="id"
      [name]="field.name"
      [readonly]="props?.readonly"
      [required]="props?.required"
      [errorStateMatcher]="matcher"
      [formlyAttributes]="field"
      [tabIndex]="props?.tabindex"
      [placeholder]="props?.placeholder ?? 'Pick one'"
      type="text"
      [formControl]="formControl"
      [matAutocomplete]="autocomplete"
      (input)="search.set(input.value)"
      (focus)="search.set(input.value)"
    />
    <mat-autocomplete requireSelection #autocomplete="matAutocomplete">
      @for (option of filteredCountries(); track option.officialName) {
        <mat-option [value]="option.commonName">
          {{ option.commonName }}
        </mat-option>
      }
    </mat-autocomplete>
    @if (showError) {
      <mat-error>
        <formly-validation-message [field]="field"></formly-validation-message>
      </mat-error>
    }
  </mat-form-field>`,
  styles: `
    mat-form-field {
      display: block;
    }
  `,
})
export class CountriesAutocompleteComponent extends FieldType<FieldTypeConfig<FormlyFieldProps>> {
  countriesService = inject(CountriesService);
  countries = this.countriesService.countries;
  search = signal('');
  filteredCountries = computed(() =>
    this.countries().filter((country) => this._filter(country, this.search())),
  );
  matcher = new CustomErrorStateMatcher();

  private _filter(country: Country, search: string): unknown {
    if (!search) return true;

    const value = search.toLowerCase();
    return (
      country.commonName.toLowerCase().includes(value) ||
      country.officialName.toLowerCase().includes(value)
    );
  }
}
